/**
 * Central de Atendimento — Adm gerencia as conversas de suporte abertas pelos usuários.
 * Combina os modelos aprovados: agrupado por empresa, etiqueta de assunto por conversa,
 * e um painel de contexto do usuário ao lado do chat. Vive dentro do `SupportCenterPage`,
 * que é quem possui o `PageShell` — esta view é só o miolo do chat em si.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send, CheckCircleOutline, ReplayOutlined } from '@mui/icons-material';
import { formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../../stores/authStore';
import { useSupportInbox } from '../../hooks/useSupportInbox';
import type { SupportConversation, SupportConversationSubject } from '../../services/supportChatService';

const SUBJECT_LABEL: Record<SupportConversationSubject, string> = {
  tecnico: 'Técnico',
  financeiro: 'Financeiro',
  duvida: 'Dúvida',
};

const SUBJECT_COLOR: Record<SupportConversationSubject, 'warning' | 'primary' | 'default'> = {
  tecnico: 'warning',
  financeiro: 'primary',
  duvida: 'default',
};

function nomeDaEmpresa(conversa: SupportConversation): string {
  return conversa.company?.tradeName || conversa.company?.legalName || 'Empresa';
}

function agruparPorEmpresa(
  conversas: SupportConversation[]
): { empresa: string; conversas: SupportConversation[] }[] {
  const grupos = new Map<string, SupportConversation[]>();
  for (const conversa of conversas) {
    const chave = nomeDaEmpresa(conversa);
    const lista = grupos.get(chave) ?? [];
    lista.push(conversa);
    grupos.set(chave, lista);
  }
  return Array.from(grupos.entries()).map(([empresa, lista]) => ({ empresa, conversas: lista }));
}

function horaCurta(data: string): string {
  return new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export const SupportInboxView: React.FC = () => {
  const { user } = useAuth();
  const {
    conversations,
    selectedConversation,
    messages,
    isLoadingList,
    isSending,
    selectConversation,
    reply,
    accept,
    setResolved,
  } = useSupportInbox();
  const [input, setInput] = useState('');

  // O texto de "há quanto tempo" só recalcula quando algo re-renderiza a lista — sem isso,
  // ele fica parado em "1 segundo" até a próxima atualização de dados (que pode demorar até
  // um minuto), mesmo o tempo de verdade passando na tela. Este relógio força o recálculo a
  // cada poucos segundos, sem depender de nenhum dado novo vir do servidor.
  const [, forcarRecalculoDeTempo] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => forcarRecalculoDeTempo((n) => n + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    const texto = input;
    // Só limpa o campo depois de confirmar o envio — senão um erro (por exemplo, a conversa
    // foi atribuída a outra pessoa enquanto essa estava escrevendo) apaga a resposta digitada.
    void reply(texto).then((ok) => {
      if (ok) setInput('');
    });
  }, [input, reply]);

  const grupos = agruparPorEmpresa(conversations);
  const naFila = Boolean(selectedConversation && selectedConversation.assignedToId === null);
  const minha = Boolean(
    selectedConversation &&
      selectedConversation.assignedToId !== null &&
      String(selectedConversation.assignedToId) === String(user?.id ?? '')
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 260px)',
        minHeight: 480,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
        {/* Coluna 1 — conversas agrupadas por empresa */}
        <Box sx={{ width: 300, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto' }}>
          {isLoadingList && conversations.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Nenhuma conversa de suporte ainda nesta empresa.
            </Typography>
          ) : (
            <List disablePadding>
              {grupos.map((grupo) => (
                <Box key={grupo.empresa}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5 }}
                  >
                    {grupo.empresa}
                  </Typography>
                  {grupo.conversas.map((conversa) => (
                    <ListItemButton
                      key={conversa.id}
                      selected={conversa.id === selectedConversation?.id}
                      onClick={() => selectConversation(conversa.id)}
                      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {conversa.subject && (
                          <Chip
                            size="small"
                            label={SUBJECT_LABEL[conversa.subject]}
                            color={SUBJECT_COLOR[conversa.subject]}
                            variant="outlined"
                          />
                        )}
                        {conversa.status === 'open' &&
                          (conversa.assignedToId === null ? (
                            <Chip size="small" label="● Fila" color="warning" variant="outlined" />
                          ) : (
                            <Chip
                              size="small"
                              label={
                                String(conversa.assignedToId) === String(user?.id ?? '')
                                  ? 'Com você'
                                  : `Com ${conversa.assignedTo?.name ?? 'alguém'}`
                              }
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {conversa.user?.name ?? 'Usuário'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" flexShrink={0}>
                          {formatDistanceToNowStrict(new Date(conversa.updatedAt), { locale: ptBR })}
                        </Typography>
                      </Stack>
                      {conversa.status === 'resolved' && (
                        <Chip size="small" label="Resolvida" variant="outlined" />
                      )}
                    </ListItemButton>
                  ))}
                </Box>
              ))}
            </List>
          )}
        </Box>

        {/* Coluna 2 — chat da conversa selecionada */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!selectedConversation ? (
            <Box sx={{ m: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                Selecione uma conversa para responder.
              </Typography>
            </Box>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {selectedConversation.user?.name ?? 'Usuário'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {nomeDaEmpresa(selectedConversation)}
                  </Typography>
                  {selectedConversation.acceptedAt && (
                    <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 600 }}>
                      ✓ {minha ? 'Você aceitou' : `${selectedConversation.assignedTo?.name ?? 'Alguém'} aceitou`} às{' '}
                      {horaCurta(selectedConversation.acceptedAt)}
                    </Typography>
                  )}
                </Box>
                {naFila ? (
                  <Button size="small" variant="contained" color="warning" disabled={isSending} onClick={() => void accept()}>
                    Aceitar
                  </Button>
                ) : (
                  minha && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={selectedConversation.status === 'open' ? <CheckCircleOutline /> : <ReplayOutlined />}
                      onClick={() => setResolved(selectedConversation.status === 'open')}
                    >
                      {selectedConversation.status === 'open' ? 'Marcar como resolvida' : 'Reabrir'}
                    </Button>
                  )
                )}
              </Stack>

              <Stack spacing={1.5} sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default' }}>
                {messages.map((message) => {
                  const mine = String(message.senderId) === String(user?.id ?? '');
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        alignSelf: mine ? 'flex-end' : 'flex-start',
                        maxWidth: '70%',
                        bgcolor: mine ? 'primary.main' : 'background.paper',
                        color: mine ? 'primary.contrastText' : 'text.primary',
                        border: mine ? 'none' : '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2">{message.text}</Typography>
                    </Box>
                  );
                })}
              </Stack>

              {naFila ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'warning.light' }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Ninguém aceitou esta conversa ainda.
                  </Typography>
                  <Button size="small" variant="contained" color="warning" disabled={isSending} onClick={() => void accept()}>
                    Aceitar e responder
                  </Button>
                </Stack>
              ) : minha ? (
                <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Escreva sua resposta..."
                    value={input}
                    disabled={isSending}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSend();
                    }}
                  />
                  <IconButton color="primary" onClick={handleSend} disabled={isSending} aria-label="Enviar resposta">
                    <Send fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    Sendo atendida por {selectedConversation.assignedTo?.name ?? 'outra pessoa'} — você só pode
                    acompanhar.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Coluna 3 — contexto do usuário */}
        {selectedConversation && (
          <Box sx={{ width: 260, borderLeft: '1px solid', borderColor: 'divider', p: 2, overflowY: 'auto' }}>
            <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: 'primary.main' }}>
              {(selectedConversation.user?.name ?? '?').slice(0, 2).toUpperCase()}
            </Avatar>
            <Typography variant="subtitle2" fontWeight={700} textAlign="center">
              {selectedConversation.user?.name ?? 'Usuário'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 2 }}>
              {selectedConversation.user?.email}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="overline" color="text.secondary" display="block">
              Empresa
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {nomeDaEmpresa(selectedConversation)}
            </Typography>

            {selectedConversation.user?.whatsapp && (
              <>
                <Typography variant="overline" color="text.secondary" display="block">
                  WhatsApp
                </Typography>
                <Typography variant="body2">{selectedConversation.user.whatsapp}</Typography>
              </>
            )}
          </Box>
        )}
    </Box>
  );
};

export default SupportInboxView;
